import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { COURSE_API_BASE } from "../utils/apiConfig";

/**
 * Fetch and consume a user's energy.
 *
 * const { energy, maxEnergy, nextEnergyAt, loading, consuming, error, consumeEnergy, refetch } = useEnergy(userId);
 *
 * const res = await consumeEnergy(1);
 * if (!res.success) console.log(res.errType);
 */
export default function useEnergy(userId) {
    const [energy, setEnergy] = useState(null);
    const [maxEnergy, setMaxEnergy] = useState(null);
    const [nextEnergyAt, setNextEnergyAt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [consuming, setConsuming] = useState(false);
    const [error, setError] = useState(null);

    const abortRef = useRef(null);
    const timerRef = useRef(null);

    const applyEnergy = (data) => {
        setEnergy(data.energy);
        setMaxEnergy(data.max_energy);
        setNextEnergyAt(data.next_energy_at ?? null);
    };

    const url = `${COURSE_API_BASE}/${userId}/energy`;

    // GET energy
    const refetch = useCallback(async () => {
        if (!userId) return { success: false };

        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setLoading(true);
        setError(null);

        try {
            const { data } = await axios.get(url, {
                withCredentials: true, // web session auth
                signal: controller.signal,
            });

            if (data.success) {
                applyEnergy(data);
                return { success: true, ...data };
            }

            setError('Failed to load energy');
            return { success: false };
        } catch (e) {
            if (axios.isCancel(e)) return { success: false, aborted: true };

            const message =
                e.response?.status === 429
                    ? 'Too many requests, please slow down'
                    : e.response?.data?.message || e.message;
            setError(message);
            return { success: false };
        } finally {
            if (abortRef.current === controller) setLoading(false);
        }
    }, [userId, url]);

    // POST consume energy
    const consumeEnergy = useCallback(
        async (amount = 1) => {
            if (!userId) return { success: false };

            setConsuming(true);
            setError(null);

            try {
                const { data } = await axios.post(
                    url,
                    { amount },
                    { withCredentials: true }
                );

                if (data.success) {
                    applyEnergy(data);
                    return { success: true, ...data };
                }

                return { success: false, errType: data.errType };
            } catch (e) {
                const status = e.response?.status;
                const data = e.response?.data;

                // 400 -> { success: false, errType } (e.g. not enough energy)
                if (status === 400) {
                    return { success: false, errType: data?.errType, errors: data?.errors };
                }

                if (status === 429) setError('Too many requests, please slow down');
                else setError(data?.message || e.message);

                return { success: false, errType: status ? 'server' : 'network' };
            } finally {
                setConsuming(false);
            }
        },
        [userId, url]
    );

    // initial load / user change
    useEffect(() => {
        refetch();
        return () => abortRef.current?.abort();
    }, [refetch]);

    // refetch automatically when the next energy point is due
    useEffect(() => {
        clearTimeout(timerRef.current);
        if (!nextEnergyAt || energy == null || energy >= maxEnergy) return;

        const delay = new Date(nextEnergyAt).getTime() - Date.now();
        if (Number.isNaN(delay)) return;

        // small buffer so the server has definitely ticked over
        timerRef.current = setTimeout(refetch, Math.max(delay, 0) + 500);
        return () => clearTimeout(timerRef.current);
    }, [nextEnergyAt, energy, maxEnergy, refetch]);

    return {
        energy,
        maxEnergy,
        nextEnergyAt,
        loading,
        consuming,
        error,
        consumeEnergy,
        refetch,
    };
}